import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Modal from "../Modal";
import { useState, useEffect } from "react";
import ReportTemplateList from "./ReportTemplateList";
import ReportTemplateSettings from "./ReportTemplateSettings";
import { useUser } from "src/hooks/useUser";
import Input from "../Inputs/Input";
import { updateReportTemplate } from "src/services/reportTemplate";
import { ReportTemplate } from "@/types";

interface Props {
  setPrinting?: (value: boolean) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  initialEditing?: string | null;
  onClose?: () => void;
  clientData?: any;
  scenarioData?: any;
}

const ReportTemplateModal = ({
  setPrinting,
  open,
  setOpen,
  initialEditing = null,
  onClose,
  clientData,
  scenarioData,
}: Props) => {
  const [editing, setEditing] = useState<string | null>(initialEditing);
  const [customTemplate, setCustomTemplate] = useState<ReportTemplate | null>(
    null,
  );
  const { user, updateUser } = useUser();

  // Create a custom template when editing is "custom"
  useEffect(() => {
    if (editing === "custom") {
      setCustomTemplate({
        id: "custom",
        name: "Custom Report",
        settings: [],
      });
    } else {
      setCustomTemplate(null);
    }
  }, [editing]);

  const updateTemplate = (newTemplate: Partial<ReportTemplate>) => {
    if (editing === "custom" && customTemplate) {
      // Update the custom template state
      setCustomTemplate({ ...customTemplate, ...newTemplate });
    } else {
      // Update saved template
      updateUser({
        reportTemplates: user?.reportTemplates.map((t) =>
          t.id === template.id ? { ...t, ...newTemplate } : t,
        ),
      });
      updateReportTemplate(template.id, {
        ...template,
        ...newTemplate,
      }).then(() => {});
    }
  };

  useEffect(() => {
    if (initialEditing !== null) {
      setEditing(initialEditing);
    }
  }, [initialEditing]);

  const handleClose = () => {
    setEditing(null);
    setCustomTemplate(null);
    setOpen(false);
    setCustomTemplate(null);
    if (onClose) {
      onClose();
    }
  };

  const template =
    editing === "custom"
      ? customTemplate
      : (user?.reportTemplates.find((temp) => temp.id === editing) as any);

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <div className="h-[80vh]" style={{ width: "min(75vw, 1400px)" }}>
        <div className="flex justify-between w-full items-start">
          <div className="flex gap-8 items-center">
            {editing !== null && !setPrinting && (
              <div>
                <ArrowLeftIcon
                  className="w-10 h-10  cursor-pointer"
                  onClick={() => {
                    setEditing(null);
                  }}
                />
              </div>
            )}
            {template && editing !== "custom" && (
              <div className="mb-5">
                <Input
                  label="Template Name"
                  vertical
                  width="text-xl! w-[300px]!"
                  value={template?.name}
                  setValue={(name) =>
                    updateTemplate({
                      name,
                    })
                  }
                />
              </div>
            )}
            {editing === "custom" && (
              <div className="mb-5">
                <h2 className="text-xl font-semibold">Custom Report</h2>
              </div>
            )}
          </div>

          <div>
            <XMarkIcon
              className="w-10 h-10 cursor-pointer"
              onClick={handleClose}
            />
          </div>
        </div>
        {editing === null ? (
          <ReportTemplateList setEditing={setEditing} />
        ) : template ? (
          <div className="flex justify-center w-full">
            <ReportTemplateSettings
              template={template}
              clientData={clientData}
              scenarioData={scenarioData}
              onPrintStart={() => {
                onClose?.();
                setPrinting?.(true);
              }}
              onClose={handleClose}
              onPrintEnd={() => setPrinting?.(false)}
              customSetTemplate={
                editing === "custom" ? updateTemplate : undefined
              }
            />
          </div>
        ) : (
          <div className="flex justify-center items-center h-64">
            <div>Loading template...</div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReportTemplateModal;
